import { useLocation } from 'react-router-dom';
import { Box, Button, Modal, Paper } from '@mui/material';
import useChallengeModalStyles from './useChallengeModalStyles';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { User } from '../../../../types';
import { currentUserSelector } from '../../../../redux/slices/userSlice';
import useGlobalStyles from '../../../useGlobalStyles';
import { playGame, setOpponent } from '../../../../redux/slices/gameParrotSlice';

/**
 * Interface representing the props for the Connect Four Modal component.
 *
 * - open Boolean representing whether or not the modal is open.
 * - challenged The user who is being challenged.
 * - openTicTacToe The function to open the tic tac toe modal.
 * - openConnectFour The function to open the connect four modal.
 * - openBattleship The function to open the battleship modal.
 * - closeModal The function to close the modal.
 */
interface ChallengeModalProps {
  open: boolean;
  challenged: User | undefined;
  openTicTacToe: () => void;
  openConnectFour: () => void;
  openBattleship: () => void;
  closeModal: () => void;
}

/**
 * Connect Four Modal component renders a connect four game board.
 *
 * @param open Boolean representing whether or not the modal is open.
 * @param challenged The user who is being challenged.
 * @param openTicTacToe The function to open the tic tac toe modal.
 * @param openConnectFour The function to open the connect four modal.
 * @param openBattleship The function to open the battleship modal.
 * @param closeModal The function to close the modal.
 */
const ChallengeModal = ({
  open,
  challenged,
  openTicTacToe,
  openConnectFour,
  openBattleship,
  closeModal,
}: ChallengeModalProps) => {
  const { classes } = useChallengeModalStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);

  /**
   * Function to handle the initiation of a game, sending the request to take the first
   * turn to the user being challenged.
   *
   * @param game - The type of game being initiated, either tic tac toe, connect four,
   * or battleship.
   */
  const handleStart = (game: string) => {
    dispatch(
      playGame({
        going: currentUser?._id || '',
        waiting: challenged?._id || '',
        data: [game, location.pathname.includes('/home') ? 'challenge' : 'fun'],
        type: 'initiation',
      }),
    );
    dispatch(setOpponent(challenged));

    if (game === 'tictactoe') openTicTacToe();
    if (game === 'connectfour') openConnectFour();
    if (game === 'battleship') openBattleship();
    closeModal();
  };

  return (
    <Modal open={open} onClose={() => {}}>
      <Box className={classes.modalStyles} component={Paper}>
        <span className={globalClasses.h2}>GameParrot</span>
        <img
          className={`${classes.gameParrotIcon}`}
          src={`${process.env.PUBLIC_URL}/assets/branding/gameparrot.png`}
        />
        <span className={`${globalClasses.h6} ${classes.challengeUser}`}>
          {location.pathname.includes('/home') ? 'Challenge' : 'Play'}{' '}
          <span style={{ fontWeight: '600' }}>{challenged?.username}</span>
          {location.pathname.includes('/home') ? ' for 1 point!' : ' for fun!'}
        </span>
        <Button
          className={`${globalClasses.button} ${classes.challengeButton}`}
          onClick={() => handleStart('tictactoe')}>
          Tic Tac Toe
        </Button>
        <Button
          className={`${globalClasses.button} ${classes.challengeButton}`}
          onClick={() => handleStart('connectfour')}>
          Connect Four
        </Button>
        <Button
          className={`${globalClasses.button} ${classes.challengeButton}`}
          onClick={() => handleStart('battleship')}>
          Battleship
        </Button>
        <Button
          className={`${globalClasses.button} ${classes.cancelButton}`}
          onClick={() => closeModal()}>
          Cancel
        </Button>
      </Box>
    </Modal>
  );
};

export default ChallengeModal;
